import { useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Divider, Form, Input, Result, Row, Select, Space, Spin, Steps, Tag, Typography } from 'antd'
import { CheckCircleOutlined, DatabaseOutlined, LockOutlined, RocketOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import api from '../../utils/api'
import './InstallationPage.css'

const { Title, Text, Paragraph } = Typography
const stepFields = [['installation_token'],['company_name'],['admin_name','admin_email','admin_password','confirm_password'],['accounting_template']]

export default function InstallationPage({ status, onInstalled }) {
  const [form] = Form.useForm()
  const [step,setStep] = useState(0)
  const [loading,setLoading] = useState(false)
  const [error,setError] = useState(status?.last_error || null)
  const [completed,setCompleted] = useState(false)
  const environment = status?.environment || {}
  const steps = useMemo(() => [
    { title:'ตรวจสอบระบบ', icon:<DatabaseOutlined/> }, { title:'ข้อมูลกิจการ', icon:<RocketOutlined/> },
    { title:'ผู้ดูแลระบบ', icon:<SafetyCertificateOutlined/> }, { title:'ยืนยันติดตั้ง', icon:<CheckCircleOutlined/> }
  ], [])

  const verifyToken = async () => {
    const { installation_token:token } = await form.validateFields(['installation_token'])
    setLoading(true); setError(null)
    try {
      await api.post('/api/installation/verify-token', {}, { headers:{ 'X-Installation-Token':token } })
      setStep(1)
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'ไม่สามารถตรวจสอบ Installation Token ได้')
    } finally { setLoading(false) }
  }

  const next = async () => {
    if (step === 0) return verifyToken()
    await form.validateFields(stepFields[step])
    setStep(current => Math.min(current + 1, 3))
  }

  const install = async () => {
    const values = await form.validateFields()
    const installation_token = values.installation_token
    const payload = { ...values }
    delete payload.installation_token
    delete payload.confirm_password
    setLoading(true); setError(null)
    try {
      await api.post('/api/installation/install', payload, { timeout:180000, headers:{ 'X-Installation-Token':installation_token } })
      setCompleted(true)
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || 'ติดตั้งระบบไม่สำเร็จ')
    } finally { setLoading(false) }
  }

  if (completed) return <div className="installation-shell"><Result status="success" title="ติดตั้ง ATOMSOFT ERP สำเร็จ"
    subTitle="ฐานข้อมูลและบัญชี Super Admin พร้อมใช้งานแล้ว"
    extra={<Button type="primary" size="large" onClick={()=>{onInstalled?.();window.location.assign('/login')}}>เข้าสู่ระบบ</Button>} /></div>

  return <div className="installation-page">
    <div className="installation-brand"><div className="installation-logo"><RocketOutlined/></div><div><strong>ATOMSOFT ERP</strong><span>System Installation</span></div></div>
    <Card className="installation-card" bordered={false}>
      <div className="installation-heading"><div><Title level={2}>ติดตั้งระบบ</Title><Paragraph type="secondary">ตั้งค่าระบบครั้งแรกสำหรับฐานข้อมูลใหม่</Paragraph></div><Tag color="blue">Version {status?.app_version || '-'}</Tag></div>
      <Steps current={step} items={steps} responsive className="installation-steps" />
      <Divider />
      {error && <Alert type="error" showIcon message="ไม่สามารถดำเนินการได้" description={error} closable onClose={()=>setError(null)} style={{marginBottom:20}} />}
      <Spin spinning={loading} tip={step===3?'กำลังสร้างฐานข้อมูล กรุณาอย่าปิดหน้านี้':'กำลังตรวจสอบ'}>
        <Form form={form} layout="vertical" initialValues={{ branch_name:'สำนักงานใหญ่', accounting_template:'thai_standard' }} requiredMark="optional">
          <section hidden={step!==0} className="installation-section">
            <Title level={4}>ตรวจสอบสภาพแวดล้อม</Title>
            <Row gutter={[12,12]}>
              <Col xs={24} md={8}><div className="environment-tile"><CheckCircleOutlined/><span>Database</span><strong>{environment.database_connected?'เชื่อมต่อแล้ว':'ไม่พร้อม'}</strong></div></Col>
              <Col xs={24} md={8}><div className="environment-tile"><CheckCircleOutlined/><span>Node.js</span><strong>{environment.node || '-'}</strong></div></Col>
              <Col xs={24} md={8}><div className="environment-tile"><CheckCircleOutlined/><span>MySQL</span><strong>{environment.mysql || '-'}</strong></div></Col>
            </Row>
            <Alert type="info" showIcon style={{margin:'20px 0'}} message={`ระบบจะสร้างโครงสร้างฐานข้อมูล ${environment.baseline_tables || 0} ตาราง`} description="ระบบจะไม่คัดลอกข้อมูลลูกค้า เอกสาร หรือธุรกรรมจากฐานข้อมูลอื่น" />
            <Form.Item name="installation_token" label="Installation Token" rules={[{required:true,message:'กรุณากรอก Installation Token'}]} extra="ดู Token ได้จากหน้าต่าง Console ของ Backend ตอนเริ่ม Server">
              <Input.Password size="large" prefix={<LockOutlined/>} placeholder="วาง Installation Token ที่นี่" autoComplete="off" />
            </Form.Item>
          </section>

          <section hidden={step!==1} className="installation-section">
            <Title level={4}>ข้อมูลกิจการ</Title>
            <Row gutter={16}>
              <Col xs={24} md={16}><Form.Item name="company_name" label="ชื่อกิจการ/บริษัท" rules={[{required:true,message:'กรุณากรอกชื่อกิจการ'}]}><Input size="large"/></Form.Item></Col>
              <Col xs={24} md={8}><Form.Item name="branch_name" label="ชื่อสาขา"><Input size="large"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="tax_id" label="เลขประจำตัวผู้เสียภาษี"><Input size="large" maxLength={20}/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="company_phone" label="เบอร์โทรศัพท์"><Input size="large"/></Form.Item></Col>
              <Col xs={24}><Form.Item name="company_address" label="ที่อยู่"><Input.TextArea rows={3}/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="company_email" label="อีเมลกิจการ" rules={[{type:'email',message:'รูปแบบอีเมลไม่ถูกต้อง'}]}><Input size="large"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="website" label="เว็บไซต์"><Input size="large" placeholder="https://"/></Form.Item></Col>
            </Row>
          </section>

          <section hidden={step!==2} className="installation-section">
            <Title level={4}>สร้างบัญชี Super Admin</Title>
            <Alert type="warning" showIcon message="บัญชีนี้มีสิทธิ์สูงสุดในระบบ กรุณาใช้รหัสผ่านที่คาดเดายาก" style={{marginBottom:20}} />
            <Row gutter={16}>
              <Col xs={24} md={12}><Form.Item name="admin_name" label="ชื่อผู้ดูแลระบบ" rules={[{required:true,message:'กรุณากรอกชื่อ'}]}><Input size="large"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="admin_email" label="อีเมลเข้าสู่ระบบ" rules={[{required:true,message:'กรุณากรอกอีเมล'},{type:'email',message:'รูปแบบอีเมลไม่ถูกต้อง'}]}><Input size="large" autoComplete="username"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="admin_password" label="รหัสผ่าน" rules={[{required:true},{min:10,message:'อย่างน้อย 10 ตัวอักษร'}]}><Input.Password size="large" autoComplete="new-password"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="confirm_password" label="ยืนยันรหัสผ่าน" dependencies={['admin_password']} rules={[{required:true},{validator:(_,value)=>!value||value===form.getFieldValue('admin_password')?Promise.resolve():Promise.reject(new Error('รหัสผ่านไม่ตรงกัน'))}]}><Input.Password size="large" autoComplete="new-password"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="admin_phone" label="เบอร์โทรศัพท์"><Input size="large"/></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="admin_address" label="ที่อยู่/หน่วยงาน"><Input size="large"/></Form.Item></Col>
            </Row>
          </section>

          <section hidden={step!==3} className="installation-section">
            <Title level={4}>ข้อมูลเริ่มต้นและยืนยัน</Title>
            <Form.Item name="accounting_template" label="แม่แบบผังบัญชี" rules={[{required:true}]}>
              <Select size="large" options={[{value:'thai_standard',label:'ผังบัญชีมาตรฐานสำหรับธุรกิจไทย (แนะนำ)'},{value:'empty',label:'ไม่สร้างผังบัญชี — ตั้งค่าด้วยตนเองภายหลัง'}]}/>
            </Form.Item>
            <div className="installation-summary"><SafetyCertificateOutlined/><div><strong>พร้อมติดตั้ง</strong><p>ระบบจะสร้างฐานข้อมูล, Role และสิทธิ์, เมนู, หน่วยนับ, Storage แบบ Local และบัญชี Super Admin</p></div></div>
            <Alert type="warning" showIcon message="เมื่อเริ่มติดตั้ง กรุณาอย่าปิด Browser หรือหยุด Backend จนกว่าจะเสร็จ" />
          </section>
        </Form>
      </Spin>
      <Divider />
      <div className="installation-actions"><Button size="large" disabled={step===0||loading} onClick={()=>setStep(current=>current-1)}>ย้อนกลับ</Button><Space>{step<3?<Button type="primary" size="large" loading={loading} onClick={next}>{step===0?'ตรวจสอบ Token':'ถัดไป'}</Button>:<Button type="primary" size="large" icon={<RocketOutlined/>} loading={loading} onClick={install}>ติดตั้งระบบ</Button>}</Space></div>
    </Card>
    <Text className="installation-footer">© {new Date().getFullYear()} ATOMSOFT ERP · Secure Installation</Text>
  </div>
}
